import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Dashboard.css';

interface DriverInfo {
  id: number;
  username: string;
  name: string;
  phone?: string;
  is_admin?: boolean;
  assigned_apartments: string[];
}

interface ApartmentStatus {
  apartment_name: string;
  apartment_address?: string | null;
  entrance_password?: string | null;
  chart_id: number;
  total_count: number;
  total_house_count?: number;
  completed_count: number;
  is_all_completed: boolean;
  cooler_bag_count: number;
  first_completed_at?: string | null;
  last_completed_at?: string | null;
}

const DeliveryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [driverInfo, setDriverInfo] = useState<DriverInfo | null>(null);
  const [selectedApartment, setSelectedApartment] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(getDefaultDeliveryDateKst());
  const [apartmentStatuses, setApartmentStatuses] = useState<ApartmentStatus[]>([]);
  const [startLockMessage, setStartLockMessage] = useState<string>('');

  useEffect(() => {
    // 버전 체크 (페이지 로드 시)
    const checkVersionOnFocus = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/version`);
        if (response.ok) {
          const data = await response.json();
          const serverVersion = data.app_version;
          const localVersion = localStorage.getItem('app_version') || '1.0.0';
          
          if (serverVersion !== localVersion || data.force_refresh) {
            console.log(`페이지 활성화: 버전 업데이트 감지 ${localVersion} → ${serverVersion}`);
            localStorage.setItem('app_version', serverVersion);
            window.location.reload();
          }
        }
      } catch (err) {
        // 버전 체크 실패해도 계속 진행
      }
    };
    
    // 로그인 정보 확인
    const token = localStorage.getItem('token');
    const savedDriver = localStorage.getItem('driverInfo');
    
    if (!token || !savedDriver) {
      navigate('/login');
      return;
    }
    
    const driver = JSON.parse(savedDriver);
    setDriverInfo(driver);
    
    // 관리자면 대시보드로 리다이렉트
    if (driver.is_admin) {
      navigate('/admin');
      return;
    }

    fetchStartStatus(token);
    
    // 첫 아파트 자동 선택
    if (driver.assigned_apartments.length > 0 && !selectedApartment) {
      setSelectedApartment(driver.assigned_apartments[0]);
    }
    
    // 페이지 활성화 시 버전 체크
    checkVersionOnFocus();
    
    // 포커스 이벤트 리스너 추가 (백그라운드에서 돌아올 때)
    const handleFocus = () => {
      checkVersionOnFocus();
      fetchStartStatus(token);
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [navigate, selectedApartment]);

  // 선택된 날짜가 변경되면 아파트 상태 가져오기
  useEffect(() => {
    if (driverInfo) {
      fetchApartmentStatuses();
    }
  }, [selectedDate, driverInfo]);

  const getApiBase = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }
    if (window.location.protocol === 'https:') {
      return window.location.origin;
    }
    return `http://${hostname}:8000`;
  };

  const fetchApartmentStatuses = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !driverInfo) return;

      const response = await fetch(
        `${getApiBase()}/api/charts/list/${selectedDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setApartmentStatuses(data);
        setStartLockMessage('');
      } else if (response.status === 423) {
        const data = await response.json().catch(() => ({}));
        const detail = data.detail || {};
        const message = detail.message || '배송 시작 확인 후 차트를 볼 수 있습니다.';
        setStartLockMessage(message);
        setApartmentStatuses([]);
        navigate('/start-check');
      } else {
        setApartmentStatuses([]);
      }
    } catch (err) {
      setApartmentStatuses([]);
    }
  };

  function formatKstDate(date: Date): string {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value || '';
    const month = parts.find((p) => p.type === 'month')?.value || '';
    const day = parts.find((p) => p.type === 'day')?.value || '';
    return `${year}-${month}-${day}`;
  }

  function getDefaultDeliveryDateKst(): string {
    const now = new Date();
    const kstHour = Number(new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Seoul',
      hour: '2-digit',
      hour12: false,
      hourCycle: 'h23',
    }).format(now)) % 24;

    if (kstHour < 12) {
      return formatKstDate(now);
    }

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatKstDate(tomorrow);
  }

  const fetchStartStatus = async (token: string) => {
    try {
      const response = await fetch(`${getApiBase()}/api/delivery-start/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.delivery_date) {
          setSelectedDate(data.delivery_date);
        }
        if (Array.isArray(data.assigned_apartments)) {
          const assignedApartments = data.assigned_apartments
            .map((name: unknown) => String(name || '').trim())
            .filter(Boolean);
          const savedDriver = localStorage.getItem('driverInfo');
          const currentDriver = savedDriver ? JSON.parse(savedDriver) : driverInfo;
          if (currentDriver) {
            const nextDriver = {
              ...currentDriver,
              assigned_apartments: assignedApartments,
            };
            localStorage.setItem('driverInfo', JSON.stringify(nextDriver));
            setDriverInfo(nextDriver);
            setSelectedApartment(current => (
              assignedApartments.includes(current) ? current : assignedApartments[0] || ''
            ));
          }
        }
      }
    } catch {
      // 배송 시작 상태 조회가 실패해도 기본 날짜로 계속 진행
    }
  };

  const formatDeliveryTime = (value?: string | null) => {
    if (!value) return '-';
    const match = String(value).match(/\d{2}:\d{2}/);
    return match ? match[0] : value;
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('driverInfo');
    navigate('/login');
  };

  if (!driverInfo) {
    return <div>로딩 중...</div>;
  }

  const statusByApartment = new Map(
    apartmentStatuses.map(status => [status.apartment_name.trim(), status])
  );
  const sortedAssignedApartments = driverInfo.assigned_apartments
    .map((apartmentName, originalIndex) => ({
      apartmentName,
      originalIndex,
      status: statusByApartment.get(apartmentName.trim()),
    }))
    .sort((a, b) => {
      const rank = (status?: ApartmentStatus) => {
        if (!status) return 2; // 배송 내용 없음은 맨 아래
        if (status.is_all_completed) return 1; // 배송 완료는 진행 중 다음
        return 0; // 진행 중/배송 대상이 가장 위
      };

      const rankDiff = rank(a.status) - rank(b.status);
      if (rankDiff !== 0) {
        return rankDiff;
      }

      return a.originalIndex - b.originalIndex;
    });

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>배송 차트</h1>
          <span className="driver-name">{driverInfo.name} 기사님</span>
        </div>
        <div className="header-right">
          <input 
            type="date" 
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="date-input-header"
          />
          <button onClick={() => setSelectedDate(getDefaultDeliveryDateKst())} className="btn-today-header">
            기본일
          </button>
          <button onClick={handleLogout} className="logout-btn">로그아웃</button>
        </div>
      </header>

      <div className="apartment-list-section">
        <h2>배정된 아파트 ({driverInfo.assigned_apartments.length}개)</h2>
        <p className="date-info">배송일: {selectedDate}</p>
        {startLockMessage && (
          <p className="start-lock-message">{startLockMessage}</p>
        )}
        <div className="apartment-cards">
          {sortedAssignedApartments.map(({ apartmentName: apt, status }) => {
            const isCompleted = status?.is_all_completed || false;
            const hasNoData = !status; // 차트가 없을 때만
            const totalHouseCount = status?.total_house_count ?? status?.total_count ?? 0;
            
            return (
              <div 
                key={apt} 
                className={`apartment-card-main ${isCompleted ? 'completed' : ''} ${hasNoData ? 'no-data' : ''}`}
                onClick={() => {
                  if (!hasNoData) {
                    navigate(`/chart/${encodeURIComponent(apt)}/${selectedDate}`);
                  }
                }}
              >
                <h3>{apt}</h3>
                <p className="delivery-date">{selectedDate}</p>
                
                {hasNoData ? (
                  <p className="no-data-info">배송 내용 없음</p>
                ) : (
                  <>
                    {status?.entrance_password && (
                      <p className="entrance-password-info">
                        공동현관: {status.entrance_password}
                      </p>
                    )}
                    {status && status.cooler_bag_count > 0 && (
                      <p className="cooler-bag-info">
                        보냉가방: {status.cooler_bag_count}개
                      </p>
                    )}
                    {status && (
                      <div className="status-info">
                        <span>총수량 {status.total_count}건</span>
                        <span>집 수 {totalHouseCount}가구</span>
                        <span>완료 {status.completed_count}/{totalHouseCount}</span>
                      </div>
                    )}
                    {status && (
                      <div className="delivery-time-info">
                        <span>첫 배송 {formatDeliveryTime(status.first_completed_at)}</span>
                        <span>최근 배송 {formatDeliveryTime(status.last_completed_at)}</span>
                      </div>
                    )}
                    <p className="click-hint">
                      {isCompleted ? '배송완료 - 클릭하여 확인' : '클릭하여 차트 보기'}
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DeliveryDashboard;
